import { Info } from 'lucide-react';
import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../molecules/popover';

// ─── FieldHint ────────────────────────────────────────────────────────────────

export interface FieldHintProps {
  /** The hint text or node shown inside the popover. */
  children: React.ReactNode;
  /** Side the popover opens towards. Defaults to "top". */
  side?: React.ComponentPropsWithoutRef<typeof PopoverContent>['side'];
}

/**
 * Inline info icon for form field labels that surfaces supplemental context
 * on click or tap — works on desktop and iOS.
 *
 * Uses `Popover` (not `Tooltip`) so it responds to tap on touch devices.
 * Place it as a child of `FormLabel` or any `<label>`:
 *
 * ```tsx
 * <FormLabel>
 *   Max Tool Calls
 *   <FieldHint>
 *     Maximum number of individual tool invocations per run.
 *   </FieldHint>
 * </FormLabel>
 * ```
 *
 * No provider needed — `Popover` is self-contained.
 */
const FieldHint = React.forwardRef<HTMLButtonElement, FieldHintProps>(
  ({ children, side = 'top' }, ref) => (
    <Popover>
      <PopoverTrigger
        ref={ref}
        type="button"
        aria-label="More info"
        className="ml-1 inline-flex cursor-pointer align-middle text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        onPointerDown={(e) => e.preventDefault()}
      >
        <Info className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent
        side={side}
        aria-label="Field hint"
        className="w-auto max-w-56 px-3 py-1.5 text-xs font-medium"
      >
        {children}
      </PopoverContent>
    </Popover>
  ),
);
FieldHint.displayName = 'FieldHint';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { FieldHint };
